import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import {
  IContact,
  IContactLeadSummary,
  ISplitContactRequest,
} from "../models/Contact";
import { DefaultResponseWithData } from "../models/system";
import apiConfig from "../utils/apiConfig";

/**
 * The other leads sharing a contact, plus the "split" action that gives one lead its own copy so
 * later edits stop rewriting the rest.
 */
export default function useContactLeads(
  contactId?: string,
  componentName = "useContactLeads"
) {
  const { get, post } = useApiHelper(apiConfig(componentName));
  const queryClient = useQueryClient();

  const contactLeads = useQuery({
    queryKey: ["contactLeads", contactId],
    queryFn: () =>
      get<DefaultResponseWithData<IContactLeadSummary[]>>(
        `/contact/${contactId}/leads`
      ),
    staleTime: 1000 * 60, // 1 minute
    enabled: !!contactId,
  });

  const split = useMutation(
    (req: ISplitContactRequest) =>
      post<ISplitContactRequest, DefaultResponseWithData<IContact>>(
        `/contact/${contactId}/split`,
        req
      ),
    {
      onSuccess: (_data, req) => {
        // The lead now points at a different contact, so anything keyed on either is stale.
        queryClient.invalidateQueries(["contactLeads"]);
        queryClient.invalidateQueries(["contact"]);
        queryClient.invalidateQueries(["contacts"]);
        queryClient.invalidateQueries(["lead", req.leadId]);
        queryClient.invalidateQueries(["leads"]);
      },
    }
  );

  return {
    contactLeads,
    split,
  };
}
